import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Input, Modal } from '../ui'
import { api, ApiError } from '../../api/client'
import { useSystemIntegrations } from '../../api/hooks'
import type { AccessSystem, SystemIntegration } from '../../api/types'
import { useAuthStore } from '../../stores/auth'

/** Public DNS of the cloud FlexC receiver the panel dials out to. */
export const FLEXC_RECEIVER_HOST = 'flexc.ostrel.se'
export const FLEXC_RECEIVER_PORT = 50000
export const DEFAULT_POLL_INTERVAL = 10

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

/** 64 random hex chars (32 bytes = AES-256 key material), generated client-side. */
export function generateFlexcKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32))
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** Fixed panel-side values (set under Kommunikationer → FlexC on a Vanderbilt SPC). */
export const PANEL_CONSTANTS: { label: string; value: string }[] = [
  { label: 'Mottagarens IP', value: FLEXC_RECEIVER_HOST },
  { label: 'Port', value: String(FLEXC_RECEIVER_PORT) },
  { label: 'Protokollversion', value: '2' },
  { label: 'Nätverk', value: 'TCP/IP' },
  { label: 'Alltid ansluten', value: 'ON' },
]

/**
 * Provisioning cheat-sheet for an acre System's `flexc` integration: the exact
 * values to type into the SPC panel, with the per-panel fields editable
 * (each saves through the full-replace integration PUT, config merged
 * client-side against a fresh read). The encryption key is write-only —
 * generated here, shown exactly once, never retrievable afterwards.
 */
export function FlexCProvisioningCard({ system }: { system: AccessSystem }) {
  const integrations = useSystemIntegrations(system.unique_id)
  const flexc = integrations.data?.find((i) => i.type === 'flexc')
  if (system.provider !== 'acre' || !flexc) return null
  // key remounts the form when the integration row is replaced (e.g. after
  // a delete + re-add), so field state initializes from fresh config.
  return <FlexCProvisioningForm key={flexc.unique_id} system={system} integration={flexc} />
}

function FlexCProvisioningForm({
  system,
  integration,
}: {
  system: AccessSystem
  integration: SystemIntegration
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const isAdmin = useAuthStore((s) => s.customer?.role === 'admin')
  const queryClient = useQueryClient()

  const [receiverId, setReceiverId] = useState(
    typeof integration.config.flexc_receiver_id === 'string'
      ? integration.config.flexc_receiver_id
      : '',
  )
  const [panelId, setPanelId] = useState(
    typeof integration.config.flexc_panel_id === 'string'
      ? integration.config.flexc_panel_id
      : '',
  )
  const [pollInterval, setPollInterval] = useState(
    typeof integration.config.flexc_poll_interval === 'number'
      ? String(integration.config.flexc_poll_interval)
      : String(DEFAULT_POLL_INTERVAL),
  )
  const [shownKey, setShownKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [scope] })

  /** Merge a config patch into a FRESH read of the integration, then PUT the
   *  full-replace body (config is whole-object replace server-side). */
  const saveConfig = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const current = await api.systemIntegration(system.unique_id, integration.unique_id)
      const config = { ...current.config }
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === '') delete config[key]
        else config[key] = value
      }
      return api.updateSystemIntegration(system.unique_id, integration.unique_id, {
        enabled: current.enabled,
        flags: current.flags,
        config,
      })
    },
    onSuccess: invalidate,
  })

  /** Generate the key client-side, store it write-only, THEN reveal it once. */
  const generateKey = useMutation({
    mutationFn: async () => {
      const key = generateFlexcKey()
      const current = await api.systemIntegration(system.unique_id, integration.unique_id)
      await api.updateSystemIntegration(system.unique_id, integration.unique_id, {
        enabled: current.enabled,
        flags: current.flags,
        config: current.config,
        secrets: { flexc_key: key },
      })
      return key
    },
    onSuccess: (key) => {
      invalidate()
      setCopied(false)
      setShownKey(key)
    },
  })

  const saving = saveConfig.isPending || generateKey.isPending
  const actionError = saveConfig.error ?? generateKey.error

  return (
    <Card title="FlexC provisioning">
      <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
        Enter these values on the Vanderbilt SPC panel under{' '}
        <span className="font-medium text-ostrel-ink">Kommunikationer → FlexC</span>. The panel
        dials out to our receiver and holds one permanent session.
      </p>

      <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        {PANEL_CONSTANTS.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-3">
            <dt className="text-xs text-ostrel-ink-muted">{row.label}</dt>
            <dd className="tnum font-mono text-sm font-medium text-ostrel-ink">{row.value}</dd>
          </div>
        ))}
      </dl>

      {actionError && (
        <p className="mt-3 rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
          {errDetail(actionError)}
        </p>
      )}

      <div className="mt-4 grid gap-4 border-t border-ostrel-line pt-4 sm:grid-cols-3">
        <div className="flex flex-col gap-2">
          <Input
            label="Mottagar-ID"
            value={receiverId}
            onChange={(e) => setReceiverId(e.target.value)}
            disabled={!isAdmin || saving}
            autoComplete="off"
          />
          <Button
            variant="navy"
            size="sm"
            disabled={!isAdmin || saving}
            title={isAdmin ? undefined : 'Requires the admin role'}
            onClick={() => saveConfig.mutate({ flexc_receiver_id: receiverId.trim() })}
          >
            Save
          </Button>
        </div>
        <div className="flex flex-col gap-2">
          <Input
            label="Enhets-ID"
            value={panelId}
            onChange={(e) => setPanelId(e.target.value)}
            disabled={!isAdmin || saving}
            autoComplete="off"
          />
          <Button
            variant="navy"
            size="sm"
            disabled={!isAdmin || saving}
            title={isAdmin ? undefined : 'Requires the admin role'}
            onClick={() => saveConfig.mutate({ flexc_panel_id: panelId.trim() })}
          >
            Save
          </Button>
        </div>
        <div className="flex flex-col gap-2">
          <Input
            label="Poll interval (s)"
            type="number"
            min={1}
            value={pollInterval}
            onChange={(e) => setPollInterval(e.target.value)}
            disabled={!isAdmin || saving}
          />
          <Button
            variant="navy"
            size="sm"
            disabled={!isAdmin || saving || Number(pollInterval) < 1}
            title={isAdmin ? undefined : 'Requires the admin role'}
            onClick={() => saveConfig.mutate({ flexc_poll_interval: Number(pollInterval) })}
          >
            Save
          </Button>
        </div>
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-ostrel-ink-muted">
        Enhets-ID is globally unique across all panels and operator-assigned — pick an unused
        number. We are the poll master; the interval defaults to {DEFAULT_POLL_INTERVAL} s.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-ostrel-line pt-4">
        <span className="text-xs text-ostrel-ink-muted">Kryptering</span>
        <Button
          variant="navy"
          size="sm"
          disabled={!isAdmin || saving}
          title={isAdmin ? undefined : 'Requires the admin role'}
          onClick={() => generateKey.mutate()}
        >
          {generateKey.isPending ? 'Generating…' : 'Generate key'}
        </Button>
        <span className="text-[11px] leading-relaxed text-ostrel-ink-muted">
          Generates a new 64-character hex key (AES-256) and stores it write-only. The stored key
          is never shown again — generating a new one replaces it.
        </span>
      </div>

      <Modal open={shownKey !== null} title="FlexC encryption key" onClose={() => setShownKey(null)}>
        <p className="text-xs leading-relaxed text-ostrel-ink-muted">
          Enter this key on the panel now. It is stored write-only and{' '}
          <strong className="text-ostrel-ink">cannot be retrieved later</strong> — this is the only
          time it is shown.
        </p>
        <code
          data-testid="flexc-key"
          className="tnum mt-3 block break-all rounded-sm border border-ostrel-line bg-ostrel-sand px-3 py-2.5 font-mono text-sm text-ostrel-ink"
        >
          {shownKey}
        </code>
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (shownKey) void navigator.clipboard.writeText(shownKey).then(() => setCopied(true))
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button variant="navy" size="sm" onClick={() => setShownKey(null)}>
            Done
          </Button>
        </div>
      </Modal>
    </Card>
  )
}
